import type { ReactNode } from "react";

// The workspace's tools (Cards, Code, Files). On big screens they're the tabs of the left panel; in
// the compact layout (Review 17, C62) they fold into a narrow rail, and picking one opens its panel
// as a drawer over the game. Both are drawn from the same list, so hiding a tool (lesson-by-lesson
// unlocks, decision 90 D46) is a filter on WORKSPACE_TOOLS.

export type ToolId = "cards" | "code" | "files";

export interface ToolDescriptor {
  id: ToolId;
  label: string;
  /** What it's for, in a tooltip on the rail. */
  hint: string;
  icon: ReactNode;
}

const svg = (children: ReactNode) => (
  <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="square" strokeLinejoin="miter" aria-hidden="true">
    {children}
  </svg>
);

export const WORKSPACE_TOOLS: ToolDescriptor[] = [
  {
    id: "cards",
    label: "Cards",
    hint: "Cards: what your program does, and when",
    icon: svg(
      <>
        <rect x="3" y="4" width="18" height="6" />
        <rect x="3" y="14" width="18" height="6" />
      </>,
    ),
  },
  {
    id: "code",
    label: "Code",
    hint: "Code: read and change your files",
    icon: svg(<path d="M8 7l-5 5 5 5M16 7l5 5-5 5M13.5 4l-3 16" />),
  },
  {
    id: "files",
    label: "Files",
    hint: "Files: your project's files and notes",
    icon: svg(
      <>
        <path d="M6 3h8l4 4v14H6z" />
        <path d="M14 3v4h4" />
      </>,
    ),
  },
];

/**
 * The compact layout's rail. `open` is whether the drawer is showing; picking the open tool again
 * closes it.
 */
export function ToolsRail({ tools, active, open, onPick }: { tools: ToolDescriptor[]; active: ToolId; open: boolean; onPick: (id: ToolId) => void }) {
  return (
    <nav className="tools-rail" aria-label="Tools">
      <div role="tablist" aria-orientation="vertical">
        {tools.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={open && active === t.id}
            aria-controls="tool-panel"
            title={t.hint}
            onClick={() => onPick(t.id)}
          >
            {t.icon}
            <span className="rail-label">{t.label}</span>
          </button>
        ))}
      </div>
    </nav>
  );
}
